/// <amd-module name="Repos/Common/Permissions" />
import { GitVersionDescriptor, GitVersionType } from "Repos/Common/Contracts/Git";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssSecurityService } from "VSS/Platform/Security";
import { traceException } from "VSS/Platform/Trace";
import { getGitRepository } from "./VCViewModelDataProviderService";

export const enum GitRepositoryPermissions {
    None = 0,
    Administer = 1,
    GenericRead = 2,
    GenericContribute = 4,
    ForcePush = 8,
    CreateBranch = 16,
    CreateTag = 32,
    ManageNote = 64,
    PolicyExempt = 128,
    CreateRepository = 256,
    DeleteRepository = 512,
    RenameRepository = 1024,
    EditPolicies = 2048,
    RemoveOthersLocks = 4096,
    ManagePermissions = 8192,
    PullRequestContribute = 16384,
    PullRequestBypassPolicy = 32768,
    ViewAdvSecAlerts = 65536,
    DismissAdvSecAlerts = 131072,
    ManageAdvSecScanning = 262144,
    ProjectLevelPermissions = 491518,
    RepositoryLevelPermissions = 491262,
    BranchLevelPermissions = 14476,
    NonBranchRefLevelPermissions = 8200,
    BranchesRootLevelPermissions = 14492
}

const gitSecurityNamespaceId = "2e9eb7ed-3c0a-47d4-87c1-0ffdd275fd87";
const gitSecurableRoot = "repoV2/";
const dataProviderKeyPrefix = "TFS.VersionControl.PermissionsDataProvider.SecurityToken.";
const anyBranchCreateSuffix = "AnyCreateBranch";
const ImplicitManageAdvSecSuffix = "ImplicitManageAdvSec";

const versionControlPermissionsDataProviderId = "ms.vss-code-web.permissions-data-provider";
interface IVersionControlPermissionsData {
    [key: string]: string;
}

export interface IReposPermissionsService extends IVssService {
    /**
     * Determine whether or not the current user has the specified repository permission
     *
     * @param permission Repository permission to check
     * @param version Optional version to scope permissions to
     * @param repositoryId Optional id of the repository to check. The page's current repository is used by default. An empty string ("") should be used to check project level permissions.
     * @param projectId Optional id of the project to check. The page's current project is used by default
     */
    hasPermission(permission: GitRepositoryPermissions, version?: GitVersionDescriptor, repositoryId?: string, projectId?: string): boolean;

    /**
     * Determine whether or not the current user has the specified favorites permission
     *
     * @param permission Favorites permission to check
     */
    hasFavoritesPermission(permission: number): boolean;

    /**
     * Determine whether or not the current user has the specified settings permission
     *
     * @param permission Settings permission to check
     */
    hasSettingsPermission(permission: number): boolean;

    /**
     * Determine whether or not the current user has the specified repository permission asynchronously when data is not already loaded into the provider
     *
     * @param permission Repository permission to check
     * @param version Optional version to scope permissions to
     * @param repositoryId Optional id of the repository to check. The page's current repository is used by default. An empty string ("") should be used to check project level permissions.
     * @param projectId Optional id of the project to check. The page's current project is used by default
     */
    hasPermissionAsync(
        permission: GitRepositoryPermissions,
        version?: GitVersionDescriptor,
        repositoryId?: string,
        projectId?: string
    ): Promise<boolean>;
}

class ReposPermissionsService extends VssService implements IReposPermissionsService {
    private asyncCache: { [repoId: string]: Promise<void> };
    private defaultProjectId: string | undefined;
    private defaultRepositoryId: string | undefined;
    private permissionData: IVersionControlPermissionsData;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        this.permissionData = contributionService.getData<IVersionControlPermissionsData>(versionControlPermissionsDataProviderId) || {};

        const project = getTFSPageData(this.pageContext)!.project;
        if (project) {
            this.defaultProjectId = project.id;
        }

        try {
            // getGitRepository throws an exception for project admins that should be able to still see permissions on the
            // new repos settings page when they have read permission set to DENY.
            const repo = getGitRepository(this.pageContext);
            if (repo) {
                this.defaultRepositoryId = repo.id;
            }
        } catch (error) {
            console.error(error);
            traceException(this.pageContext, "Repos", "ReposPermissionsService._serviceStart", error);
        }

        this.asyncCache = {};
    }

    public hasPermission(permission: GitRepositoryPermissions, version?: GitVersionDescriptor, repositoryId?: string, projectId?: string): boolean {
        return this.hasPermissionHelper(permission, version, repositoryId, projectId);
    }

    public hasFavoritesPermission(permission: number): boolean {
        return this.hasFrameworkPermission("favorites", permission);
    }

    public hasSettingsPermission(permission: number): boolean {
        return this.hasFrameworkPermission("settings/global", permission);
    }

    public hasPermissionAsync(permission: GitRepositoryPermissions, version?: GitVersionDescriptor, repositoryId?: string, projectId?: string) {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const cacheKey = repositoryId || (repositoryId === undefined ? (this.defaultRepositoryId || "undefined") : "empty");
        let result = this.asyncCache[cacheKey];
        if (!result) {
            result = contributionService
                .getDataAsync<IVersionControlPermissionsData>(versionControlPermissionsDataProviderId, {
                    repositoryId: repositoryId
                })
                .then(permissionData => {
                    // merge provider data
                    this.permissionData = { ...this.permissionData, ...permissionData };
                });

            this.asyncCache[cacheKey] = result;
        }

        return result.then(() => this.hasPermissionHelper(permission, version, repositoryId, projectId));
    }

    private hasFrameworkPermission(name: string, permission: number): boolean {
        const tokenKey = `${dataProviderKeyPrefix}${name}`;
        let token = this.permissionData[tokenKey];

        if (token) {
            const slashIndex = token.indexOf("/");
            if (slashIndex >= 0) {
                const namespaceId = token.substr(0, slashIndex);
                token = token.substr(slashIndex + 1);

                const securityService = this.pageContext.getService<IVssSecurityService>("IVssSecurityService");
                return securityService.hasPermission(namespaceId, token, permission);
            }
        }

        if ($DEBUG) {
            console.warn(`Invalid token for ${name}: ${token}`);
        }

        return false;
    }

    private hasPermissionHelper(
        permission: GitRepositoryPermissions,
        version?: GitVersionDescriptor,
        repositoryId?: string,
        projectId?: string,
        permissionData?: IVersionControlPermissionsData
    ): boolean {
        if (projectId === undefined) {
            projectId = this.defaultProjectId;
        }

        if (repositoryId === undefined) {
            repositoryId = this.defaultRepositoryId;
        }

        let token: string | undefined;

        if (version && version.versionType === GitVersionType.Branch) {
            token = (permissionData || this.permissionData)[`${dataProviderKeyPrefix}${projectId}/${repositoryId}/refs/heads/${version.version}`];
            if (token && token.indexOf(gitSecurityNamespaceId + "/") === 0) {
                token = token.substr(gitSecurityNamespaceId.length + 1);
            }
        }

        if (!token) {
            token = gitSecurableRoot;
            if (projectId) {
                token += projectId + "/";
                if (repositoryId) {
                    token += repositoryId + "/";
                }
            }
        }

        const securityService = this.pageContext.getService<IVssSecurityService>("IVssSecurityService");
        let result = securityService.hasPermission(gitSecurityNamespaceId, token, permission);

        if (!!repositoryId && permission === GitRepositoryPermissions.CreateBranch && !version && !result) {
            result = !!this.permissionData[`${dataProviderKeyPrefix}${projectId}/${repositoryId}/${anyBranchCreateSuffix}`];
        }

        if (permission == GitRepositoryPermissions.ManageAdvSecScanning && !result) {
            if (!!repositoryId) {
                result = !!this.permissionData[`${dataProviderKeyPrefix}${projectId}/${repositoryId}/${ImplicitManageAdvSecSuffix}`];
            }
            else if (!!projectId) {
                result = !!this.permissionData[`${dataProviderKeyPrefix}${projectId}/${ImplicitManageAdvSecSuffix}`];
            }
            else {
                result = !!this.permissionData[`${dataProviderKeyPrefix}${ImplicitManageAdvSecSuffix}`];
            }
        }

        return result;
    }
}

Services.add("IReposPermissionsService", { serviceFactory: ReposPermissionsService });
